import {
  CalendarDate,
  fromDate,
  getLocalTimeZone,
  toCalendarDate,
  today
} from '@internationalized/date';
import { maybe, rd, RemoteData } from '@passionware/monads';
import { forwardRef, useEffect, useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericDateFilter } from 'v5/api/_common/query/filters/GenericDateFilter';
import { Button } from 'v5/design-system/Button';
import { DatePicker } from 'v5/design-system/DatePicker';
import { Input } from 'v5/design-system/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from 'v5/design-system/Select';
import { Switch } from 'v5/design-system/Switch';
import { parseHumanDate } from 'v5/platform/lang/parseHumanDate';
import {
  AbstractFilterWidget,
  AbstractFilterWidgetOperatorTrigger,
  AbstractFilterWidgetValueTrigger,
  FilterWidgetPropsBase
} from './_private/AbstractFilterWidget';

export type DatePresetKey = string;

export type DateFilterOperator = Exclude<
  GenericDateFilter<DatePresetKey>['operator'],
  'preset'
>;

export interface DateFilterWidgetProps<P extends DatePresetKey>
  extends FilterWidgetPropsBase<GenericDateFilter<P>> {
  config: {
    // future idea: resolver can match-back the range to a preset
    // useResolver: () => RemoteData<DateFilterResolver<P>>;
    useOptions: () => RemoteData<{ value: P; label: string }[]>;
    /** The operators offered besides the presets, for a host that can't store the others; all by default. */
    operators?: DateFilterOperator[];
  };
  filterField?: string;
}

// human-input parsing removed in favor of exact payload dates

const DateFilterWidgetInner = <P extends DatePresetKey>(
  {
    value,
    onValueChange,
    config,
    filterName,
    filterIcon,
    onRemove,
    filterField,
    ...rest
  }: DateFilterWidgetProps<P>,
  ref: React.Ref<HTMLDivElement>
) => {
  const { t, i18n } = useTranslation();
  const tr = (key: string) => (t as unknown as (k: string) => string)(key);
  const options = config.useOptions();
  const offers = (operator: DateFilterOperator) =>
    config.operators?.includes(operator) ?? true;
  const [valueOpen, setValueOpen] = useState(false);
  const form = useForm<{
    single: Date | null;
    rangeFrom: Date | null;
    rangeTo: Date | null;
    humanInput: string;
    mode: 'single' | 'range';
  }>({
    defaultValues: {
      single: null,
      rangeFrom: null,
      rangeTo: null,
      humanInput: '',
      mode: value.operator === 'between' ? 'range' : 'single'
    }
  });

  const values = form.watch();
  const locale = i18n.language || undefined;
  const switchId = useId();

  const calendarDateToDate = (date?: CalendarDate | null) =>
    date ? date.toDate(getLocalTimeZone()) : null;

  const dateToCalendarDateOrNull = (date: Date | null) =>
    date ? toCalendarDate(fromDate(date, getLocalTimeZone())) : null;

  const currentOperator = useMemo<
    'between' | 'greaterThan' | 'lessThan' | 'equal'
  >(() => {
    switch (value.operator) {
      case 'between':
        return 'between';
      case 'greaterThan':
        return 'greaterThan';
      case 'equal':
        return 'equal';
      case 'lessThan':
        return 'lessThan';
      case 'preset':
        return 'between';
    }
  }, [value]);

  const operatorSelectValue = useMemo(() => {
    if (value.operator === 'preset') {
      return `preset:${String(value.value)}`;
    }
    if (value.operator === 'equal') {
      return 'equal';
    }
    return currentOperator;
  }, [currentOperator, value]);

  const renderValueLabel = <Q extends DatePresetKey>(
    v: GenericDateFilter<Q>,
    translate: (k: string) => string,
    lang: string
  ): string => {
    const locale: string | undefined = lang || undefined;
    const fmt = (d?: CalendarDate | null) => {
      if (!d) return '';
      const js = d.toDate(getLocalTimeZone());
      return js.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    if (v.operator === 'between') {
      return `${fmt(v.value.from ?? null)} → ${fmt(v.value.to ?? null)}`;
    }
    if (v.operator === 'greaterThan') {
      return fmt(v.value);
    }
    if (v.operator === 'lessThan') {
      return fmt(v.value);
    }
    if (v.operator === 'equal') {
      return fmt(v.value);
    }
    // preset is handled by hiding the value chip, so this should not normally be called.
    return '';
  };

  useEffect(() => {
    if (!valueOpen) return;
    form.reset({
      single:
        value.operator !== 'between' && value.operator !== 'preset'
          ? calendarDateToDate(value.value)
          : null,
      rangeFrom:
        value.operator === 'between'
          ? calendarDateToDate(value.value.from ?? null)
          : null,
      rangeTo:
        value.operator === 'between'
          ? calendarDateToDate(value.value.to ?? null)
          : null,
      humanInput: '',
      mode:
        value.operator === 'between' || value.operator === 'preset'
          ? 'range'
          : 'single'
    });
  }, [valueOpen, value, form]);

  useEffect(() => {
    const text = values.humanInput?.trim();
    if (!text) return;
    const handle = setTimeout(() => {
      const parsed = parseHumanDate(text);
      if (!parsed) return;
      console.log('parsed', parsed);
      if (parsed.range) {
        form.setValue('mode', 'range', { shouldDirty: true });
        form.setValue('rangeFrom', parsed.range.from ?? null, {
          shouldDirty: true
        });
        form.setValue('rangeTo', parsed.range.to ?? null, {
          shouldDirty: true
        });
      } else if (parsed.single) {
        form.setValue('mode', 'single', { shouldDirty: true });
        form.setValue('single', parsed.single ?? null, { shouldDirty: true });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [values.humanInput, form]);

  const formatDate = (date?: Date | null) =>
    date
      ? date.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : '';

  const formattedPreview = useMemo(() => {
    const parsed = values.humanInput ? parseHumanDate(values.humanInput) : null;
    if (parsed?.range) {
      return `${formatDate(parsed.range.from)} → ${formatDate(parsed.range.to)}`;
    }
    if (parsed?.single) {
      return formatDate(parsed.single);
    }
    if (values.mode === 'range') {
      if (values.rangeFrom || values.rangeTo) {
        return `${formatDate(values.rangeFrom)} → ${formatDate(values.rangeTo)}`;
      }
      return '';
    }
    return formatDate(values.single);
  }, [
    values.humanInput,
    values.mode,
    values.rangeFrom,
    values.rangeTo,
    values.single,
    locale
  ]);

  const canApply =
    values.mode === 'range'
      ? Boolean(values.rangeFrom && values.rangeTo)
      : Boolean(values.single);

  const submitForm = form.handleSubmit(() => {
    if (values.mode === 'range') {
      const from = dateToCalendarDateOrNull(values.rangeFrom);
      const to = dateToCalendarDateOrNull(values.rangeTo);
      if (from && to) {
        onValueChange({
          operator: 'between',
          value: { from, to }
        });
        setValueOpen(false);
      }
      return;
    }
    const single = dateToCalendarDateOrNull(values.single);
    if (single) {
      const nextOperator =
        currentOperator === 'greaterThan'
          ? 'greaterThan'
          : currentOperator === 'lessThan'
            ? 'lessThan'
            : 'equal';
      onValueChange({
        operator: nextOperator as GenericDateFilter<P>['operator'],
        value: single
      } as GenericDateFilter<P>);
      setValueOpen(false);
    }
  });

  return (
    <>
      <AbstractFilterWidget
        ref={ref}
        filterName={filterName}
        filterIcon={filterIcon}
        onRemove={onRemove}
        filterField={filterField}
        {...rest}
        operatorSlot={
          <Select
            value={operatorSelectValue}
            onValueChange={next => {
              if (next.startsWith('preset:')) {
                const presetId = next.slice('preset:'.length) as P;
                onValueChange({
                  operator: 'preset',
                  value: presetId
                });
                return;
              }

              if (next === 'between') {
                const prevRange: { from?: CalendarDate; to?: CalendarDate } =
                  value.operator === 'between' ? value.value : {};
                const base = today(getLocalTimeZone());
                onValueChange({
                  operator: 'between',
                  value: {
                    from: maybe.getOrElse(prevRange.from, base),
                    to: maybe.getOrElse(prevRange.to, base.add({ days: 1 }))
                  }
                });
              } else if (next === 'greaterThan') {
                onValueChange({
                  operator: 'greaterThan',
                  value: today(getLocalTimeZone())
                });
              } else if (next === 'lessThan') {
                onValueChange({
                  operator: 'lessThan',
                  value: today(getLocalTimeZone())
                });
              } else {
                onValueChange({
                  operator: 'equal',
                  value: today(getLocalTimeZone())
                });
              }
            }}
          >
            <SelectTrigger asChild>
              <AbstractFilterWidgetOperatorTrigger data-element="operator-trigger">
                {value.operator === 'preset'
                  ? rd.tryMap(
                      options,
                      options =>
                        options.find(option => option.value === value.value)
                          ?.label
                    )
                  : currentOperator === 'between'
                    ? tr('patterns:DateFilterWidget.operator.between')
                    : currentOperator === 'greaterThan'
                      ? tr('patterns:DateFilterWidget.operator.after')
                      : currentOperator === 'lessThan'
                        ? tr('patterns:DateFilterWidget.operator.before')
                        : tr('patterns:DateFilterWidget.operator.equal')}
              </AbstractFilterWidgetOperatorTrigger>
            </SelectTrigger>
            <SelectContent>
              {offers('between') && (
                <SelectItem value="between">
                  {tr('patterns:DateFilterWidget.operator.between')}
                </SelectItem>
              )}
              {offers('greaterThan') && (
                <SelectItem value="greaterThan">
                  {tr('patterns:DateFilterWidget.operator.after')}
                </SelectItem>
              )}
              {offers('lessThan') && (
                <SelectItem value="lessThan">
                  {tr('patterns:DateFilterWidget.operator.before')}
                </SelectItem>
              )}
              {offers('equal') && (
                <SelectItem value="equal">
                  {tr('patterns:DateFilterWidget.operator.equal')}
                </SelectItem>
              )}
              <div className="my-1 h-px bg-border-muted" />
              <div className="px-2 py-1 text-xs text-fg-muted">Presets</div>
              {rd
                .journey(options)
                .wait(null)
                .catch(() => null)
                .map(options =>
                  options.map(option => (
                    <SelectItem
                      key={option.value}
                      value={`preset:${String(option.value)}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))
                )}
            </SelectContent>
          </Select>
        }
        valueSlot={
          value.operator === 'preset' ? null : (
            <Popover open={valueOpen} onOpenChange={setValueOpen}>
              <PopoverTrigger asChild>
                <AbstractFilterWidgetValueTrigger data-element="value-trigger">
                  {(() => {
                    const label = renderValueLabel(value, tr, i18n.language);
                    if (!label) {
                      return (
                        <span className="text-fg-muted">
                          {tr('patterns:DateFilterWidget.emptyValue')}
                        </span>
                      );
                    }
                    return <span>{label}</span>;
                  })()}
                </AbstractFilterWidgetValueTrigger>
              </PopoverTrigger>
              <PopoverContent
                className="p-3 space-y-3 min-w-96"
                align="start"
                onInteractOutside={() => {
                  void submitForm();
                }}
              >
                <form className="space-y-3" onSubmit={submitForm}>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">
                      {tr('patterns:DateFilterCustomDialog.enterHumanDate')}
                    </div>
                    <Input
                      placeholder={tr(
                        'patterns:DateFilterCustomDialog.humanPlaceholder'
                      )}
                      {...form.register('humanInput')}
                      onBlur={event => {
                        const parsed = parseHumanDate(
                          event.target.value.trim()
                        );
                        if (!parsed) return;
                        if (parsed.range) {
                          form.setValue('mode', 'range', { shouldDirty: true });
                          form.setValue(
                            'rangeFrom',
                            parsed.range.from ?? null,
                            {
                              shouldDirty: true
                            }
                          );
                          form.setValue('rangeTo', parsed.range.to ?? null, {
                            shouldDirty: true
                          });
                        } else if (parsed.single) {
                          form.setValue('mode', 'single', {
                            shouldDirty: true
                          });
                          form.setValue('single', parsed.single ?? null, {
                            shouldDirty: true
                          });
                        }
                      }}
                    />
                    <div className="text-xs text-fg-muted">
                      {formattedPreview
                        ? `${tr(
                            'patterns:DateFilterCustomDialog.willApply'
                          )} ${formattedPreview}`
                        : tr('patterns:DateFilterCustomDialog.examples')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-fg-muted">
                    <Switch
                      id={switchId}
                      checked={values.mode === 'range'}
                      onCheckedChange={checked => {
                        form.setValue('mode', checked ? 'range' : 'single', {
                          shouldDirty: true
                        });
                        if (checked) {
                          form.setValue('single', null, { shouldDirty: true });
                        } else {
                          form.setValue('rangeFrom', null, {
                            shouldDirty: true
                          });
                          form.setValue('rangeTo', null, {
                            shouldDirty: true
                          });
                        }
                      }}
                      aria-label="Toggle range mode"
                    />
                    <label htmlFor={switchId} className="text-sm text-fg-muted">
                      {form.watch('mode') === 'range'
                        ? tr('patterns:DateFilterCustomDialog.range')
                        : tr('patterns:DateFilterCustomDialog.single')}
                    </label>
                  </div>
                  {values.mode === 'range' ? (
                    <DatePicker
                      mode="range"
                      className="w-full"
                      value={{
                        from: values.rangeFrom ?? undefined,
                        to: values.rangeTo ?? undefined
                      }}
                      onChange={range => {
                        const fromDate =
                          range &&
                          typeof range === 'object' &&
                          'from' in (range as Record<string, unknown>)
                            ? ((range as { from?: Date }).from ?? null)
                            : null;
                        const toDate =
                          range &&
                          typeof range === 'object' &&
                          'to' in (range as Record<string, unknown>)
                            ? ((range as { to?: Date }).to ?? null)
                            : null;
                        form.setValue('rangeFrom', fromDate ?? null, {
                          shouldDirty: true
                        });
                        form.setValue('rangeTo', toDate ?? null, {
                          shouldDirty: true
                        });
                      }}
                    />
                  ) : (
                    <DatePicker
                      mode="single"
                      className="w-full"
                      value={values.single ?? undefined}
                      onChange={date => {
                        form.setValue(
                          'single',
                          date instanceof Date ? date : null,
                          { shouldDirty: true }
                        );
                      }}
                    />
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => setValueOpen(false)}
                    >
                      {tr('patterns:DateFilterWidget.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      type="submit"
                      disabled={!canApply}
                    >
                      {tr('patterns:DateFilterWidget.apply')}
                    </Button>
                  </div>
                </form>
              </PopoverContent>
            </Popover>
          )
        }
      />
    </>
  );
};

export const DateFilterWidget = forwardRef(DateFilterWidgetInner) as <
  P extends DatePresetKey
>(
  props: DateFilterWidgetProps<P> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof DateFilterWidgetInner>;
